/**
 * Shared pieces for the pickers (components/pickers/*) and the Manage screens
 * built on them (Groups, Assign, Campaigns; plan docs/plans/lms-plan.md 8.2,
 * section 13; WP-43). The class strings are the design skill's recipes
 * (.claude/skills/siteoperations-design/SKILL.md, G13): cards rounded-xl,
 * controls rounded-lg, table cells py-3, micro-labels text-[10px] uppercase
 * tracking-widest, semantic pills always carrying a word, dark: classes on
 * everything. Buttons and fields come from frontend/src/ui/styles, imported by
 * file path (never the ui barrel, 2.3a).
 */
import type { ReactNode } from 'react';
import { format } from 'date-fns';
import type { LmsPlatformRole } from '../../api/client';
import type { CampaignOutcome } from '../../api/campaigns';

/** A card or panel (the design skill's card recipe). */
export const cardCls = 'rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800';

/** A card holding a table: the table runs to the card's edges. */
export const tableCardCls = 'overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800';

export const tableCls = 'min-w-full divide-y divide-gray-200 dark:divide-gray-700';
export const theadCls = 'bg-gray-50 dark:bg-gray-800/50';
export const thCls = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
export const tbodyCls = 'divide-y divide-gray-200 dark:divide-gray-700';
export const tdCls = 'px-4 py-3 text-sm text-gray-900 dark:text-gray-100';
export const rowCls = 'transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50';

/** A micro-label (text-[10px] uppercase tracking-widest). */
export const microLabelCls = 'text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400';

/** Quiet meta text. */
export const metaCls = 'text-xs text-gray-500 dark:text-gray-400';

/** A section heading inside a sub-view (the tab's PageHeader holds the h1). */
export const sectionTitleCls = 'text-base font-semibold text-gray-900 dark:text-gray-100';

/** A form label. */
export const labelCls = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200';

/** A link-styled button (primary is chrome, so links are primary). */
export const linkBtnCls =
  'inline-flex min-h-[44px] items-center gap-1 rounded text-sm font-medium text-primary-700 transition-colors duration-150 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 sm:min-h-0';

/** A checkbox (16 px box inside a 44 px row on mobile). */
export const checkboxCls =
  'h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700';

/** A selectable row with a checkbox: the whole row is the 44 px target. */
export const checkRowCls =
  'flex min-h-[44px] cursor-pointer items-center gap-3 px-3 py-2 text-sm transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 sm:min-h-0';

// ---------------------------------------------------------------------------
// Pills (the design skill's status pill: a colour family always with a word)
// ---------------------------------------------------------------------------

export type PillTone = 'green' | 'amber' | 'red' | 'primary' | 'gray';

const TONES: Record<PillTone, string> = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300',
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

export function Pill({ tone, children, testId }: { tone: PillTone; children: ReactNode; testId?: string }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${TONES[tone]}`}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

/** An amber notice: something saved but not finished (a warning, never an error; 18.20). */
export function WarningNote({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div
      role="status"
      data-testid={testId}
      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
    >
      {children}
    </div>
  );
}

/** A red notice for a failed request (role alert). */
export function ErrorNote({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** A calendar day as the player shows it ("4 Sep 2026"), or an em dash for nothing. */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '\u2014' : format(d, 'd MMM yyyy');
}

/** The platform role in words (users.role). */
export const ROLE_LABELS: Record<LmsPlatformRole, string> = {
  super_admin: 'Super admin',
  administrator: 'Administrator',
  basic_user: 'Staff',
};

export function roleLabel(role: string | null | undefined): string {
  if (!role) return '\u2014';
  return (ROLE_LABELS as Record<string, string>)[role] ?? role;
}

/** A person's display name: the name, else the email, else a plain word (never an id). */
export function personName(p: { full_name?: string | null; email?: string | null }): string {
  const name = (p.full_name ?? '').trim();
  if (name) return name;
  const email = (p.email ?? '').trim();
  return email || 'Unnamed person';
}

/** A whole number of days within [min, max] typed into a field, or null (5.3 due_days, 5.4 due_days). */
export function parseWholeDays(raw: string, min: number, max: number): number | null {
  const text = raw.trim();
  if (!/^[0-9]+$/.test(text)) return null;
  const n = Number(text);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
}

/** "1 person", "3 people". */
export function peopleCount(n: number): string {
  return `${n.toLocaleString()} ${n === 1 ? 'person' : 'people'}`;
}

// ---------------------------------------------------------------------------
// Campaign outcomes (the rule of 8.2 step 4; 5.4 lms_campaign_recipients.outcome)
// ---------------------------------------------------------------------------

export const OUTCOME_LABELS: Record<CampaignOutcome, string> = {
  assigned: 'Assigned',
  already_assigned: 'Already assigned',
  already_current: 'Already current',
  skipped_inactive: 'Skipped, inactive',
};

/** What each outcome means for the person, in one line (8.2 step 4). */
export const OUTCOME_HINTS: Record<CampaignOutcome, string> = {
  assigned: "A new assignment and a notice.",
  already_assigned: "Already has the course open: it keeps the earlier due date; a pinned version re-pins it.",
  already_current: "Holds a current completion or a standing waiver: nothing is sent unless it is forced.",
  skipped_inactive: "A deactivated or deleted account: nothing is sent.",
};

export const OUTCOME_TONES: Record<CampaignOutcome, PillTone> = {
  assigned: 'primary',
  already_assigned: 'amber',
  already_current: 'green',
  skipped_inactive: 'gray',
};

export function OutcomePill({ outcome }: { outcome: CampaignOutcome }) {
  return (
    <Pill tone={OUTCOME_TONES[outcome] ?? 'gray'} testId={`outcome-${outcome}`}>
      {OUTCOME_LABELS[outcome] ?? outcome}
    </Pill>
  );
}
